// 1. Polyfills FIRST — must run before any module that uses Buffer / process.
//    See src/polyfills.js for the rationale.
import './polyfills';

// 2026-05-16 — Disable native browser scroll restoration. Without this,
// browsers restore the previous scroll position AFTER our Layout's
// scrollTo(0) on route change, leaving the user mid-page on the new
// route (typical symptom: clicking a footer link goes to the right page
// but stays at the bottom). With 'manual', Layout's scrollTo wins.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import './i18n';
import './index.css';
import App from './App.jsx';

import { initSentry } from '@/utils/sentry';
import { hasAnalyticsConsent, initAnalytics, onConsentChange, teardownAnalytics } from '@/utils/analytics';
import { initClarity, teardownClarity } from '@/utils/clarity';
import { reportWebVitals } from '@/utils/webVitals';
import { captureReferralFromUrl } from '@/utils/referralTracking';
import ErrorBoundary from '@/components/feedback/ErrorBoundary';
import ToastProvider from '@/components/feedback/ToastProvider';

// 2. Sentry first — we want it to catch any subsequent init failure.
initSentry();

// 3. If the user has already consented in a previous session, fire GA4 +
//    Clarity immediately. Otherwise wait for the CookieBanner to dispatch.
//    Clarity is privacy-friendlier than GA so we group them under the same
//    consent flag — both off until cookie_consent_analytics === 'true'.
if (hasAnalyticsConsent()) {
  initAnalytics();
  initClarity();
}
onConsentChange((granted) => {
  if (granted) {
    initAnalytics();
    initClarity();
  } else {
    // Retrait en cours de session : sans ce else, la collecte continuait
    // jusqu'au prochain reload (RGPD Art. 7(3)).
    teardownAnalytics();
    teardownClarity();
  }
});

// 4. Web Vitals collection runs unconditionally; the metrics only get
//    transmitted once GA is initialized (analytics.trackEvent is a no-op
//    until then). This way we don't lose the first-paint measurements.
reportWebVitals();

// 5. Capture le ref= au load (parrainage). Le token est stocké 30 jours,
//    réutilisé au checkout Gumroad (cf. appendReferralToCheckoutUrl).
captureReferralFromUrl();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  </StrictMode>,
);

// Retire l'écran de démarrage (#boot dans index.html) dès que React a peint
// dans #root — fondu de 350 ms. On sonde via setTimeout (et NON
// requestAnimationFrame, qui est throttlé en arrière-plan / headless et
// laisserait l'overlay bloqué au-dessus du site). Filet de sécurité : retrait
// forcé après ~6 s quoi qu'il arrive, pour ne JAMAIS masquer le site.
(function () {
  function fadeOut() {
    const boot = document.getElementById('boot');
    if (!boot) return;
    boot.style.opacity = '0';
    setTimeout(() => boot.remove(), 350);
  }
  let tries = 0;
  (function check() {
    if (!document.getElementById('boot')) return;
    const root = document.getElementById('root');
    if ((root && root.childElementCount > 0) || tries++ > 200) {
      fadeOut();
    } else {
      setTimeout(check, 30);
    }
  })();
})();
